import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Button,
  Input,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableColumnDefinition,
  createTableColumn,
  TableCellLayout,
  TableColumnSizingOptions,
  Spinner,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';
import { IContainerItem } from '../../common/ContainerSchemas';
import {
  Dismiss20Regular,
  DeleteRegular,
  ChevronDown16Filled,
} from '@fluentui/react-icons';
import './ManageAccessDialog.css';
import { useManageAccessDialogStyles } from './ManageAccessDialog.styles';
import {
  ItemPermissionsProvider,
  IItemPermission,
} from '../../providers/ItemPermissionsProvider';

export interface IManageAccessDialogProps {
  selectedItem?: IContainerItem;
  showManageAccessDialog: boolean;
  onDismiss: () => void;
  containerId: string;
  onRequestDeletePermission?: (permission: IItemPermission) => void;
  onPermissionAdded?: () => void;
  refreshCounter?: number;
  onManageAccessDialogMounted?: (ref: { focus: () => void }) => void;
}

type PermissionType = 'read' | 'write';
type PrincipalType = 'user' | 'group';

interface User {
  email: string;
  permission: PermissionType;
  id: string;
}

export const ManageAccessDialog: React.FC<IManageAccessDialogProps> = (
  props,
) => {
  const {
    selectedItem,
    showManageAccessDialog,
    onDismiss,
    containerId: containerId,
  } = props;

  const dialogSurfaceRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');
  const [selectedPermission, setSelectedPermission] =
    useState<PermissionType>('read');
  const [principalType, setPrincipalType] = useState<PrincipalType>('user');
  const [permissions, setPermissions] = useState<IItemPermission[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const manageAccessStyles = useManageAccessDialogStyles();

  // Disable body scrolling when dialog is open
  useEffect(() => {
    if (showManageAccessDialog) {
      setEmail('');
      setPrincipalType('user');
      setError(null);
    }
  }, [showManageAccessDialog]);

  const fetchPermissions = async () => {
    if (!selectedItem) return;

    setIsLoading(true);
    setError(null);
    setPermissions([]);

    try {
      const fetchedPermissions =
        await ItemPermissionsProvider.instance.getPermissions(
          containerId,
          selectedItem.id,
        );
      setPermissions(fetchedPermissions);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError('Failed to load permissions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (showManageAccessDialog && selectedItem) {
      fetchPermissions();
    }
  }, [showManageAccessDialog, selectedItem, containerId, props.refreshCounter]);

  const handleAddPermission = async () => {
    if (!selectedItem || !email) return;

    setIsLoading(true);
    setError(null);

    try {
      const isGroup = principalType === 'group';
      await ItemPermissionsProvider.instance.addPermission(
        containerId,
        selectedItem.id,
        email,
        selectedPermission,
        isGroup,
      );
      setEmail('');
      setPrincipalType('user');
      props.onPermissionAdded?.();
    } catch (err: any) {
      console.error('Error adding permission:', err);
      let errorMessage = 'Failed to add permission. Please try again.';
      if (err.message && typeof err.message === 'string') {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to get email from permission object
  const getEmailFromPermission = (permission: IItemPermission): string => {
    return permission.group_name || permission.user_principal_name!;
  };

  // Function to display friendly permission names
  const formatRoleName = (role: string): string => {
    if (role === 'read') return 'Reader';
    if (role === 'write') return 'Writer';
    return role;
  };

  const columns: TableColumnDefinition<IItemPermission>[] = [
    createTableColumn<IItemPermission>({
      columnId: 'user_group',
      renderHeaderCell: () => 'User/Group',
      renderCell: (permission) => (
        <TableCellLayout>{getEmailFromPermission(permission)}</TableCellLayout>
      ),
    }),
    createTableColumn<IItemPermission>({
      columnId: 'roles',
      renderHeaderCell: () => 'Permission',
      renderCell: (permission) => (
        <TableCellLayout>
          {permission.roles.map(formatRoleName).join(', ')}
        </TableCellLayout>
      ),
    }),
    createTableColumn<IItemPermission>({
      columnId: 'actions',
      renderHeaderCell: () => '',
      renderCell: (permission) => (
        <TableCellLayout className={manageAccessStyles.actionsCell}>
          <Button
            icon={<DeleteRegular className={manageAccessStyles.deleteIcon} />}
            onClick={() => props.onRequestDeletePermission?.(permission)}
            className="deleteButton"
          />
        </TableCellLayout>
      ),
    }),
  ];

  const columnSizingOptions: TableColumnSizingOptions = {
    email: {
      minWidth: 250,
      defaultWidth: 250,
      idealWidth: 250,
    },
    roles: {
      minWidth: 150,
      defaultWidth: 150,
    },
    actions: {
      minWidth: 20,
      defaultWidth: 20,
    },
  };

  // Function to focus the dialog surface
  const focusDialogSurface = () => {
    if (dialogSurfaceRef.current) {
      dialogSurfaceRef.current.focus();
    }
  };

  // Call focus when the dialog opens or when nested dialogs close
  useEffect(() => {
    if (showManageAccessDialog) {
      // Small delay to ensure the DOM is ready
      const timeoutId = setTimeout(() => {
        focusDialogSurface();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [showManageAccessDialog]);

  // Make the ManageAccessDialog public method available through a ref
  useEffect(() => {
    // Make the focus method available to parent components
    if (props.onManageAccessDialogMounted) {
      props.onManageAccessDialogMounted({
        focus: focusDialogSurface,
      });
    }
  }, []);

  if (!selectedItem) {
    return null;
  }

  return (
    <Dialog
      open={showManageAccessDialog}
      onOpenChange={(e, data) => !data.open && onDismiss()}
    >
      <DialogSurface
        ref={dialogSurfaceRef}
        tabIndex={0}
        className={`dialogSurface manage-access-dialog-surface ${manageAccessStyles.surfaceBg}`}
      >
        <DialogBody className={manageAccessStyles.surfaceBg}>
          <DialogTitle className="dialogTitle">Manage Access</DialogTitle>
          <Dismiss20Regular
            className="dismissIcon"
            onClick={onDismiss}
            data-testid="dismiss-icon"
          />
          <DialogContent>
            {error && (
              <div className="error" data-testid="error-message">
                {error}
              </div>
            )}
            <div>
              <div className="inputContainer">
                <div className="principalTypeContainer">
                  <RadioGroup
                    value={principalType}
                    onChange={(_, data) =>
                      setPrincipalType(data.value as PrincipalType)
                    }
                    layout="horizontal"
                  >
                    <Radio value="user" label="User" />
                    <Radio value="group" label="Group" />
                  </RadioGroup>
                </div>
                <div className="addPermissionRow">
                  <div className="combinedInputContainer">
                    <Input
                      placeholder={
                        principalType === 'user'
                          ? 'Enter email'
                          : 'Enter group name'
                      }
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="emailInput"
                    />
                    <div className="selectContainer">
                      <select
                        value={selectedPermission}
                        onChange={(e) =>
                          setSelectedPermission(
                            e.target.value as PermissionType,
                          )
                        }
                        className="permissionSelect"
                      >
                        <option value="read">Reader</option>
                        <option value="write">Writer</option>
                      </select>
                      <ChevronDown16Filled className="selectIcon" />
                    </div>
                  </div>
                  <Button
                    onClick={handleAddPermission}
                    disabled={isLoading || !email}
                    className="addButton"
                    appearance="secondary"
                    size="medium"
                  >
                    Add
                  </Button>
                </div>
              </div>

              {isLoading ? (
                <div className="spinnerContainer">
                  <Spinner label="Loading permissions..." />
                </div>
              ) : (
                <DataGrid
                  items={permissions}
                  columns={columns}
                  getRowId={(permission) => permission.id}
                  resizableColumns
                  columnSizingOptions={columnSizingOptions}
                  className="dataGridContainer"
                >
                  <DataGridHeader>
                    <DataGridRow className="gridRow">
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell className="headerCell">
                          {renderHeaderCell()}
                        </DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody<IItemPermission>>
                    {({ item, rowId }) => (
                      <DataGridRow<IItemPermission>
                        key={rowId}
                        className="gridRow"
                      >
                        {({ renderCell }) => (
                          <DataGridCell>{renderCell(item)}</DataGridCell>
                        )}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>
              )}
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
